import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { TaskBoard } from './TaskBoard';
import { useAuth } from '../auth/auth-store';
import { useCreateIntent } from '../app/create-intent';
import {
  createTask,
  listProjects,
  listSprints,
  listTasks,
  setTaskStatus,
  type TaskRow,
  type TaskStatus,
} from './api';

/**
 * Tasks.
 *
 * Status changes carry the row's version: two people moving the same task must not silently
 * overwrite each other, so the server refuses the stale one and the screen says so rather than
 * appearing to work (context.md section 199).
 */

const STATUS_TONE: Record<TaskStatus, BadgeTone> = {
  TODO: 'neutral',
  IN_PROGRESS: 'info',
  BLOCKED: 'danger',
  DONE: 'positive',
  CANCELED: 'neutral',
};

const PRIORITY_TONE: Record<string, BadgeTone> = {
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'danger',
};

const STATUSES: TaskStatus[] = ['TODO', 'IN_PROGRESS', 'BLOCKED', 'DONE', 'CANCELED'];

export function TasksPage(): React.JSX.Element {
  const client = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [view, setView] = useState<'board' | 'list'>('board');
  const [projectId, setProjectId] = useState('');
  const [sprintId, setSprintId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [status, setStatus] = useState<TaskStatus | ''>('');
  const [title, setTitle] = useState('');
  const newTask = useRef<HTMLInputElement>(null);

  // Arriving from the home screen's Criar menu puts the cursor in the field that creates one.
  useCreateIntent(() => newTask.current?.focus());
  const [error, setError] = useState<string | null>(null);

  const projects = useQuery({ queryKey: ['projects'], queryFn: listProjects });

  const project = (projects.data?.projects ?? []).find((entry) => entry.id === projectId);

  /*
   * Sprints belong to one project, so the filter only exists once a project is picked and only for
   * a project that plans in them. Changing project drops the sprint: an id from another project
   * would match nothing and look like an empty board.
   */
  const sprints = useQuery({
    queryKey: ['sprints', projectId],
    queryFn: () => listSprints(projectId),
    enabled: projectId !== '' && (project?.uses_sprints ?? false),
  });

  const tasks = useQuery({
    queryKey: ['tasks', projectId, status, assigneeId, sprintId],
    queryFn: () =>
      listTasks({
        ...(projectId ? { projectId } : {}),
        ...(status ? { status } : {}),
        ...(assigneeId ? { assigneeId } : {}),
        ...(sprintId ? { sprintId } : {}),
      }),
  });

  const projectNames = new Map(
    (projects.data?.projects ?? []).map((project) => [project.id, project.name]),
  );

  const move = useMutation({
    mutationFn: (input: { task: TaskRow; next: TaskStatus }) =>
      setTaskStatus(input.task.id, { status: input.next, expectedVersion: input.task.version }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const create = useMutation({
    mutationFn: () => createTask({ title: title.trim(), ...(projectId ? { projectId } : {}) }),
    onSuccess: () => {
      setTitle('');
      setError(null);
      void client.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const columns: Column<TaskRow>[] = [
    {
      key: 'title',
      header: t('work.task'),
      render: (task) => (
        <span className="cell-stack">
          <span className="strong">{task.title}</span>
          {task.project_id && (
            <span className="muted small">{projectNames.get(task.project_id) ?? '—'}</span>
          )}
        </span>
      ),
    },
    {
      key: 'priority',
      header: t('work.priority'),
      secondary: true,
      render: (task) => (
        <Badge tone={PRIORITY_TONE[task.priority] ?? 'neutral'}>
          {t(`work.priority.${task.priority}`)}
        </Badge>
      ),
    },
    {
      key: 'due',
      header: t('work.due'),
      secondary: true,
      render: (task) => formatDate(task.due_date),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (task) => (
        <span className="cell-inline">
          <Badge tone={STATUS_TONE[task.status]}>{t(`work.task.${task.status}`)}</Badge>

          <select
            className="select select--compact"
            value={task.status}
            disabled={move.isPending}
            aria-label={t('work.changeStatus')}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => move.mutate({ task, next: event.target.value as TaskStatus })}
          >
            {STATUSES.map((entry) => (
              <option key={entry} value={entry}>
                {t(`work.task.${entry}`)}
              </option>
            ))}
          </select>
        </span>
      ),
    },
  ];

  return (
    <div className="view view--wide">
      <PageHeader
        title={t('nav.tasks')}
        description={t('work.tasksDescription')}
        actions={
          <div className="cell-inline">
            <button
              type="button"
              className={`chip${assigneeId ? ' chip--active' : ''}`}
              onClick={() => setAssigneeId(assigneeId ? '' : (user?.id ?? ''))}
            >
              {t('work.mine')}
            </button>

            <nav className="tabs tabs--compact" aria-label={t('nav.tasks')}>
              <button
                type="button"
                className={`tab${view === 'board' ? ' is-active' : ''}`}
                onClick={() => setView('board')}
              >
                {t('work.view.board')}
              </button>
              <button
                type="button"
                className={`tab${view === 'list' ? ' is-active' : ''}`}
                onClick={() => setView('list')}
              >
                {t('work.view.list')}
              </button>
            </nav>
          </div>
        }
      />

      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          if (title.trim()) create.mutate();
        }}
      >
        <select
          className="select"
          value={projectId}
          onChange={(event) => {
            setProjectId(event.target.value);
            setSprintId('');
          }}
          aria-label={t('work.filterProject')}
        >
          <option value="">{t('work.allProjects')}</option>
          {(projects.data?.projects ?? []).map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>

        {project?.uses_sprints && (
          <select
            className="select"
            value={sprintId}
            onChange={(event) => setSprintId(event.target.value)}
            aria-label={t('work.filterSprint')}
          >
            <option value="">{t('work.allSprints')}</option>
            <option value="none">{t('sprint.waiting')}</option>
            {(sprints.data?.sprints ?? []).map((sprint) => (
              <option key={sprint.id} value={sprint.id}>
                {`${sprint.name} · ${t(`sprint.state.${sprint.state}`)}`}
              </option>
            ))}
          </select>
        )}

        {/* The board has a column per status, so a status filter there would only empty columns. */}
        {view === 'list' && (
          <select
            className="select"
            value={status}
            onChange={(event) => setStatus(event.target.value as TaskStatus | '')}
            aria-label={t('work.filterStatus')}
          >
            <option value="">{t('work.allStatuses')}</option>
            {STATUSES.map((entry) => (
              <option key={entry} value={entry}>
                {t(`work.task.${entry}`)}
              </option>
            ))}
          </select>
        )}

        <label className="field-search">
          <input
            ref={newTask}
            value={title}
            placeholder={t('work.newTaskPlaceholder')}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <button
          type="submit"
          className="button button--primary button--compact"
          disabled={title.trim() === '' || create.isPending}
        >
          {create.isPending ? t('work.creating') : t('work.newTask')}
        </button>
      </form>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {view === 'board' ? (
        <TaskBoard projectId={projectId} assigneeId={assigneeId} sprintId={sprintId} />
      ) : (
        <Panel>
          <DataTable
            tableKey="tasks"
            rows={tasks.data?.tasks}
            columns={columns}
            rowKey={(task) => task.id}
            isLoading={tasks.isLoading}
            error={tasks.error}
            onRetry={() => void tasks.refetch()}
            onRowClick={(task) => navigate(`/trabalho/tarefas/${task.id}`)}
            empty={{ title: t('work.noTasks'), description: t('work.noTasksBody') }}
          />
        </Panel>
      )}
    </div>
  );
}
